import { Database } from './database.ts';
import type { DesignSpec, MaterialKind, CadCategory } from './spec';
export type { DesignSpec, CadCategory };
export type Model = string;
export type AppLocale = 'en' | 'ja' | 'ar';

export type Message = Omit<
  Database['public']['Tables']['messages']['Row'],
  'content' | 'role'
> & {
  role: 'user' | 'assistant';
  content: Content;
};

export type Content = {
  /** Short closing line from the assistant (rendered at the end of the
   *  timeline). The full per-iteration text lives in `steps` as text steps. */
  text?: string;
  model?: Model;
  error?: string;
  artifact?: ParametricArtifact;
  /** Index into a multi-image gallery (STL-render slideshow). */
  index?: number;
  images?: string[];
  mesh?: Mesh;
  meshRenders?: string[];
  meshBoundingBox?: BoundingBox;
  meshFilename?: string;
  /** True if this turn used adaptive thinking. */
  thinking?: boolean;
  /** Chronological record of the agent's activity during this turn — the
   *  primary timeline surface in the chat UI. Append-only. */
  steps?: AgentStep[];
  /** Clarifying-question suggestions: question → suggested answers. */
  suggestions?: Record<string, string[]>;
  /** When the agent calls ask_user, the UI prompts the user with this. */
  pendingQuestion?: PendingQuestion;
  /** When a render completed in step-approval mode: continue/modify/done. */
  pendingContinue?: PendingContinue;
};

export type PendingQuestion = {
  question: string;
  suggestions?: string[];
  allowFreeText?: boolean;
};

/** Shown after a successful render in step-approval mode. The UI renders
 *  「続ける / 修正 / 完了」buttons on this card. */
export type PendingContinue = {
  summary: string;
  checkpointId: string;
};

type MeshFileType = string;

export type Mesh = {
  id: string;
  fileType: MeshFileType;
};

type BoundingBox = {
  x: number;
  y: number;
  z: number;
};

/**
 * One file produced by an artifact compile. The same `compile` may produce
 * several ArtifactFiles in different formats (preview STL, manufacturing 3MF,
 * editable SCAD source, drawing SVG/PDF).
 */
export type ArtifactFile = {
  /** File format, identifies the producer + how to render it. */
  format: 'stl' | '3mf' | 'scad' | 'svg' | 'pdf' | 'gltf' | 'glb';
  /** Firebase Storage path. */
  storagePath: string;
  /** MIME type for HTTP transport / browser handling. */
  contentType: string;
  /** What this file is FOR. Drives UI/download/handoff decisions. */
  role: 'preview' | 'manufacturing' | 'source' | 'drawing';
  /** Size in bytes. Useful for UI listings / quota; optional. */
  byteLength?: number;
  /** ISO 8601 timestamp. Useful for staleness checks; optional. */
  generatedAt?: string;
};

export type ParametricArtifact = {
  title: string;
  version: string;
  /** SCAD source as an inline string. The `parameters` editor reads/mutates this.
   *  Persistence as a downloadable .scad file lives in `files` (role: 'source'). */
  code: string;
  parameters: Parameter[];
  /** All output files for this artifact. Indexed by role + format by consumers. */
  files: ArtifactFile[];
  drawingMetadata?: DrawingMetadata;
  /** Structured JSON spec (SSOT under spec-mode). When present, `code` is a
   *  derived artifact generated by renderSpec(). Absent for legacy artifacts. */
  spec?: DesignSpec;
};

/** Convenience: find an ArtifactFile by role + (optionally) format. */
export function findArtifactFile(
  files: ArtifactFile[] | undefined,
  role: ArtifactFile['role'],
  format?: ArtifactFile['format'],
): ArtifactFile | undefined {
  if (!files) return undefined;
  for (const f of files) {
    if (f.role !== role) continue;
    if (format != null && f.format !== format) continue;
    return f;
  }
  return undefined;
}

export type ParameterOption = { value: string | number; label: string };

export type ParameterRange = { min?: number; max?: number; step?: number };

export type ParameterType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'string[]'
  | 'number[]'
  | 'boolean[]';

export type Parameter = {
  name: string;
  displayName: string;
  value: string | boolean | number | string[] | number[] | boolean[];
  defaultValue: string | boolean | number | string[] | number[] | boolean[];
  // Type should always exist, but old messages don't have it.
  type?: ParameterType;
  description?: string;
  group?: string;
  range?: ParameterRange;
  options?: ParameterOption[];
  maxLength?: number;
};

/** A single entry in the per-turn agent timeline, rendered as a stacked card
 *  in the chat UI. Append-only — the server never rewrites prior steps. */
export type AgentStep =
  | { type: 'thinking'; id: string; iteration: number; text: string; timestamp: string }
  | { type: 'text'; id: string; iteration: number; text: string; timestamp: string }
  | {
      type: 'tool_use';
      id: string;
      iteration: number;
      name: string;
      input: Record<string, unknown>;
      timestamp: string;
    }
  | {
      type: 'tool_result';
      id: string;
      iteration: number;
      toolName: string;
      ok: boolean;
      observation: string;
      timestamp: string;
    }
  | { type: 'error'; id: string; iteration: number; message: string; timestamp: string }
  | {
      type: 'checkpoint';
      id: string;
      iteration: number;
      partCount: number;
      jointCount: number;
      /** Total number of feature entries in the spec at this checkpoint. */
      featureCount?: number;
      /** Total number of parameter entries (sliders) in the spec. */
      parameterCount?: number;
      /** First 7 hex chars of sha256(spec) — visible badge so users can tell
       *  two checkpoints apart even when part/joint counts are equal. */
      specHashShort?: string;
      summary: string;
      timestamp: string;
    };

export type SectionPlane = {
  axis: 'x' | 'y' | 'z';
  position: number;
  label: string;
  description?: string;
};

export type BOMEntry = {
  number: number;
  name: string;
  specification: string;
  quantity: number;
  material: string;
  dimensions?: string;
  notes?: string;
  moduleName: string;
};

export type Accessory = {
  name: string;
  quantity: number;
  specification: string;
};

export type RecommendedView = {
  type: 'top' | 'front' | 'rear' | 'right' | 'left' | 'bottom' | 'isometric';
  label: string;
  isPrimary: boolean;
};

export type DrawingAnnotation = {
  view: string;
  position: { x: number; y: number; z: number };
  text: string;
  type: 'process' | 'callout' | 'note' | 'radius' | 'reference' | 'gdt';
};

export type ProductInfo = {
  weight?: string;
  packagingDimensions?: string[];
  packagingWeight?: string;
  packagingNotes?: string[];
};

export type SheetContent = {
  type: 'view' | 'section' | 'bom' | 'accessories' | 'notes' | 'titleBlock';
  reference: string;
  gridPosition: { row: number; col: number; rowSpan?: number; colSpan?: number };
};

export type SheetAllocation = {
  sheetNumber: number;
  sheetSize: 'A4' | 'A3' | 'A2' | 'A1';
  contents: SheetContent[];
};

export type DimensionSpec = {
  view: string;
  type: 'linear' | 'diameter' | 'radius' | 'angle' | 'chain';
  from: { x: number; y: number; z: number };
  to: { x: number; y: number; z: number };
  value: number;
  tolerance?: string;
  isReference?: boolean;
  chainSegments?: { value: number; tolerance?: string }[];
};

export type DetailView = {
  sourceView: string;
  centerX: number;
  centerY: number;
  centerZ: number;
  radius: number;
  scale: number;
  label: string;
};

// Component-level drawing: each component gets its own set of views
export type ComponentDrawing = {
  /** OpenSCAD module name (e.g., "top_plate", "side_panel") */
  componentId: string;
  /** User-facing display name. Keep technical identifiers English. */
  componentName: string;
  /** Which views to generate for this component */
  views: Array<'top' | 'front' | 'right' | 'left' | 'rear' | 'bottom'>;
  /** Component-specific dimensions */
  dimensions?: DimensionSpec[];
  /** Component-specific annotations (material callouts, process notes) */
  annotations?: DrawingAnnotation[];
};

export type DrawingMetadata = {
  /** Locale used for drawing labels, callouts, BOM headers, and table text. */
  locale?: AppLocale;
  bom: BOMEntry[];
  accessories: Accessory[];
  recommendedViews: RecommendedView[];
  sectionPlanes: SectionPlane[];
  annotations: DrawingAnnotation[];
  dimensions?: DimensionSpec[];
  detailViews?: DetailView[];
  productInfo: ProductInfo;
  sheetAllocation: SheetAllocation[];
  /** Component-level drawings — each component rendered individually */
  componentDrawings?: ComponentDrawing[];
  /** Layout mode: 'assembly' (default, 4 views of full model) or 'components' (per-part views) */
  layoutMode?: 'assembly' | 'components';
};

export type ApprovalPerson = {
  role: string;
  name: string;
  date?: string;
};

export type DrawingSettings = {
  /** Preferred app/UI language. AI internals stay English; user-facing output follows this. */
  locale?: AppLocale;
  companyName: string;
  companyNameEnglish?: string;
  logoUrl?: string;
  defaultSheetSize: 'A4' | 'A3' | 'A2' | 'A1';
  approvalChain: ApprovalPerson[];
  drawingNumberPrefix?: string;
  defaultTolerances: {
    general: string;
    hole: string;
    radius: string;
  };
  defaultProjectionMethod: 'third-angle' | 'first-angle';
  units: 'mm' | 'inch';
};

// ─── Intake (workstream A) ────────────────────────────────────────────────

/** Sub-type within a CadCategory. 21 total: 6 furniture + 6 mechanical
 *  + 4 architecture + 5 printing. Pairings are validated against `category`
 *  in the Zod schema's discriminated union. */
export type IntakeSubType =
  // furniture
  | 'table' | 'chair' | 'shelf' | 'bed' | 'desk' | 'cabinet'
  // mechanical
  | 'bracket' | 'housing' | 'gear' | 'shaft' | 'fastener_part' | 'fixture'
  // architecture
  | 'building' | 'interior_room' | 'site' | 'facade_detail'
  // printing
  | 'decorative' | 'functional' | 'replacement' | 'miniature' | 'enclosure';

export type IntakeCore = {
  /** One-line "what is it for?" */
  intendedUse: string;
  /** Overall envelope (mm). Any axis omittable. */
  size?: { length?: number; width?: number; height?: number };
  /** Primary material kind (reuses MaterialKindSchema enum from shared/spec.ts). */
  primaryMaterial?: MaterialKind;
  /** High-level fastener / assembly preference. */
  fastenerPreference?:
    | 'screws' | 'hidden' | 'glue_only' | 'mortise_tenon' | 'none' | 'mixed';
};

/** What drawings the user expects in the output. */
export type DrawingPreferences = {
  scope: 'assembly_only' | 'parts_only' | 'both';
  /** V1: 'auto' lets the agent pick; 'none' disables. User-specified is V2. */
  sectionCuts: 'auto' | 'none';
  showHoleTable: boolean;
  showBOM: boolean;
  toleranceClass: 'loose' | 'standard' | 'tight' | 'precision';
  /** V1: best-effort prompt preference. Render-time enforcement is workstream B. */
  jisStrict: boolean;
};

// ─── Sub-type details (discriminated union, all 21 variants) ──────────────

export type FurnitureTableDetails = {
  category: 'furniture'; subType: 'table';
  numSeats: 2 | 4 | 6 | 8 | 10 | 12;
  shape: 'rectangular' | 'round' | 'oval' | 'square';
  legStyle: 'straight' | 'tapered' | 'turned' | 'trestle' | 'pedestal';
  joineryType: 'mortise_tenon' | 'dowel' | 'biscuit' | 'pocket_screw' | 'metal_bracket';
  finish: 'oil' | 'lacquer' | 'paint' | 'wax' | 'unfinished';
  hasStretcher: boolean;
  hasApron: boolean;
};

export type FurnitureChairDetails = {
  category: 'furniture'; subType: 'chair';
  chairType: 'dining' | 'lounge' | 'office' | 'stool' | 'rocking';
  backStyle: 'spindle' | 'slat' | 'panel' | 'upholstered' | 'mesh';
  hasArmrests: boolean;
  legStyle: 'straight' | 'tapered' | 'splayed' | 'cabriole';
  joineryType: 'mortise_tenon' | 'dowel' | 'screw' | 'mixed';
  finish: 'oil' | 'lacquer' | 'paint' | 'wax' | 'unfinished';
};

export type FurnitureShelfDetails = {
  category: 'furniture'; subType: 'shelf';
  shelfType: 'bookcase' | 'wall_mounted' | 'cube' | 'ladder' | 'corner';
  shelfCount: number;
  adjustableShelves: boolean;
  hasBack: boolean;
  joineryType: 'dado' | 'dowel' | 'screw' | 'biscuit' | 'cam_lock';
  finish: 'oil' | 'lacquer' | 'paint' | 'wax' | 'unfinished';
};

export type FurnitureBedDetails = {
  category: 'furniture'; subType: 'bed';
  bedSize: 'single' | 'semi_double' | 'double' | 'queen' | 'king';
  hasHeadboard: boolean;
  hasFootboard: boolean;
  hasUnderStorage: boolean;
  slatCount: number;
  joineryType: 'mortise_tenon' | 'bed_bolt' | 'cam_lock' | 'screw';
  finish: 'oil' | 'lacquer' | 'paint' | 'wax' | 'unfinished';
};

export type FurnitureDeskDetails = {
  category: 'furniture'; subType: 'desk';
  deskType: 'simple' | 'pedestal_single' | 'pedestal_double' | 'l_shape' | 'standing';
  drawerCount: number;
  hasCableManagement: boolean;
  legStyle: 'straight' | 'tapered' | 'panel' | 'metal';
  joineryType: 'mortise_tenon' | 'dowel' | 'pocket_screw' | 'cam_lock';
  finish: 'oil' | 'lacquer' | 'paint' | 'wax' | 'unfinished';
};

export type FurnitureCabinetDetails = {
  category: 'furniture'; subType: 'cabinet';
  cabinetType: 'base' | 'wall' | 'tall' | 'corner' | 'island';
  doorStyle: 'flat_panel' | 'shaker' | 'raised_panel' | 'glass' | 'sliding' | 'none';
  doorCount: number;
  drawerCount: number;
  shelvesInside: number;
  hardwareStyle: 'concealed_hinge' | 'butt_hinge' | 'piano_hinge' | 'cup_hinge';
  joineryType: 'dado' | 'dowel' | 'cam_lock' | 'pocket_screw';
  finish: 'oil' | 'lacquer' | 'paint' | 'melamine' | 'unfinished';
};

export type MechanicalBracketDetails = {
  category: 'mechanical'; subType: 'bracket';
  bracketType: 'L' | 'T' | 'flat' | 'angle' | 'gusset';
  mountingPattern: 'corner' | 'flat' | 'angled' | 'offset';
  holeCount: number;
  holeSize: 'M3' | 'M4' | 'M5' | 'M6' | 'M8' | 'M10';
  holeFinish: 'through' | 'countersunk' | 'counterbored' | 'threaded';
  loadDirection: 'shear' | 'tension' | 'cantilever' | 'mixed';
  surfaceFinish: 'as_machined' | 'powder_coat' | 'anodized' | 'plated' | 'painted';
};

export type MechanicalHousingDetails = {
  category: 'mechanical'; subType: 'housing';
  housingType: 'enclosure' | 'gearbox' | 'motor' | 'pump' | 'electronics_box';
  closure: 'lid_screwed' | 'lid_snap_fit' | 'split_clamshell' | 'access_panel';
  ipRating: 'none' | 'IP54' | 'IP65' | 'IP67';
  internalFeatures: ('mounting_bosses' | 'cable_glands' | 'vent' | 'cooling_fins')[];
  fastenerSize: 'M2' | 'M2.5' | 'M3' | 'M4' | 'M5';
  surfaceFinish: 'as_machined' | 'powder_coat' | 'anodized' | 'painted';
};

export type MechanicalGearDetails = {
  category: 'mechanical'; subType: 'gear';
  gearType: 'spur' | 'helical' | 'bevel' | 'worm' | 'rack';
  module: number;
  toothCount: number;
  pressureAngle: 14.5 | 20 | 25;
  faceWidth: number;
  boreDiameter: number;
  hubStyle: 'none' | 'plain' | 'set_screw' | 'keyed' | 'press_fit';
};

export type MechanicalShaftDetails = {
  category: 'mechanical'; subType: 'shaft';
  shaftType: 'plain' | 'stepped' | 'splined' | 'threaded' | 'keyed';
  diameter: number;
  keywayPresent: boolean;
  bearingFit: 'none' | 'press' | 'sliding' | 'snug' | 'transition';
  endStyle: 'square' | 'chamfered' | 'tapered' | 'threaded' | 'splined';
  surfaceFinish: 'as_machined' | 'ground' | 'polished' | 'plated';
};

export type MechanicalFastenerPartDetails = {
  category: 'mechanical'; subType: 'fastener_part';
  fastenerType: 'screw' | 'bolt' | 'nut' | 'washer' | 'stud' | 'rivet';
  size: 'M2' | 'M3' | 'M4' | 'M5' | 'M6' | 'M8' | 'M10' | 'M12' | 'M16';
  length: number;
  head: 'flat' | 'pan' | 'socket' | 'hex' | 'button' | 'cheese' | 'countersunk';
  thread: 'full' | 'partial' | 'none';
  finish: 'plain' | 'zinc' | 'black_oxide' | 'stainless' | 'titanium';
};

export type MechanicalFixtureDetails = {
  category: 'mechanical'; subType: 'fixture';
  fixtureType: 'jig' | 'clamp' | 'guide' | 'stop' | 'workholding';
  workpieceMax: { length: number; width: number; height: number };
  clampingMethod: 'toggle' | 'cam' | 'screw' | 'pneumatic' | 'magnetic';
  baseplateMounting: 'tslot' | 'magnetic' | 'screw_down' | 'free_standing';
  toleranceClass: 'loose' | 'standard' | 'tight' | 'precision';
  hasAlignmentPins: boolean;
};

export type ArchitectureBuildingDetails = {
  category: 'architecture'; subType: 'building';
  buildingType: 'house' | 'apartment' | 'office' | 'retail' | 'warehouse' | 'mixed_use';
  scaleRatio: '1:50' | '1:100' | '1:200' | '1:500' | '1:1000';
  floorCount: number;
  roofStyle: 'flat' | 'gable' | 'hip' | 'shed' | 'mansard' | 'butterfly';
  showInterior: boolean;
  showLandscape: boolean;
};

export type ArchitectureInteriorRoomDetails = {
  category: 'architecture'; subType: 'interior_room';
  roomType: 'living' | 'bedroom' | 'kitchen' | 'bath' | 'office' | 'retail';
  scaleRatio: '1:20' | '1:50' | '1:100';
  ceilingHeight: number;
  showFurniture: boolean;
  wallTreatment: 'plain' | 'wallpaper' | 'paint' | 'paneling' | 'tile';
  showCeiling: boolean;
};

export type ArchitectureSiteDetails = {
  category: 'architecture'; subType: 'site';
  siteType: 'urban' | 'suburban' | 'rural' | 'park' | 'campus';
  scaleRatio: '1:200' | '1:500' | '1:1000' | '1:2000';
  showTopography: boolean;
  showVegetation: boolean;
  showRoads: boolean;
  buildingCount: number;
};

export type ArchitectureFacadeDetailDetails = {
  category: 'architecture'; subType: 'facade_detail';
  detailType: 'window' | 'door' | 'cornice' | 'parapet' | 'curtain_wall';
  scaleRatio: '1:5' | '1:10' | '1:20' | '1:50';
  material: 'masonry' | 'concrete' | 'metal' | 'glass' | 'wood' | 'composite';
  showSection: boolean;
  showLayers: boolean;
};

export type PrintingDecorativeDetails = {
  category: 'printing'; subType: 'decorative';
  itemType: 'vase' | 'planter' | 'art_object' | 'ornament';
  printer: 'FDM' | 'SLA' | 'SLS';
  layerHeight: number;
  shellThickness: number;
  supportPolicy: 'avoid' | 'allow_minimal' | 'allow_full';
  vaseModeCompatible: boolean;
};

export type PrintingFunctionalDetails = {
  category: 'printing'; subType: 'functional';
  functionalRole: 'mount' | 'adapter' | 'tool' | 'hook' | 'bracket' | 'gear';
  printer: 'FDM' | 'SLA' | 'SLS';
  infillPercent: number;
  shellThickness: number;
  supportPolicy: 'avoid' | 'allow_minimal' | 'allow_full';
  toleranceClass: 'loose' | 'standard' | 'tight';
  insertType: 'none' | 'heat_set' | 'press_fit' | 'self_tapping';
};

export type PrintingReplacementDetails = {
  category: 'printing'; subType: 'replacement';
  replacingType: 'knob' | 'clip' | 'gear' | 'bracket' | 'cover' | 'connector';
  printer: 'FDM' | 'SLA';
  matchOriginalDimensions: boolean;
  toleranceClass: 'loose' | 'standard' | 'tight';
  fastenerInterface: 'none' | 'screw_M3' | 'screw_M4' | 'screw_M5' | 'snap_fit';
  reinforcement: 'none' | 'thicker_walls' | 'ribs' | 'metal_insert';
};

export type PrintingMiniatureDetails = {
  category: 'printing'; subType: 'miniature';
  miniType: 'figure' | 'terrain' | 'vehicle' | 'building_kit' | 'modular_tile';
  scale: '1:35' | '1:48' | '1:72' | '1:100' | '28mm' | '32mm';
  printer: 'FDM' | 'SLA';
  layerHeight: number;
  detailLevel: 'low' | 'medium' | 'high' | 'ultra';
  basePresent: boolean;
};

export type PrintingEnclosureDetails = {
  category: 'printing'; subType: 'enclosure';
  enclosureFor: 'electronics' | 'storage' | 'food_safe' | 'outdoor' | 'general';
  printer: 'FDM' | 'SLA';
  closure: 'snap_fit' | 'screw' | 'magnet' | 'press_fit' | 'hinged_lid';
  insertType: 'none' | 'heat_set_M2' | 'heat_set_M3' | 'heat_set_M4';
  hasVents: boolean;
  hasCableEntry: boolean;
  ipRating: 'none' | 'IP54' | 'IP65';
};

export type IntakeDetails =
  | FurnitureTableDetails | FurnitureChairDetails | FurnitureShelfDetails
  | FurnitureBedDetails | FurnitureDeskDetails | FurnitureCabinetDetails
  | MechanicalBracketDetails | MechanicalHousingDetails | MechanicalGearDetails
  | MechanicalShaftDetails | MechanicalFastenerPartDetails | MechanicalFixtureDetails
  | ArchitectureBuildingDetails | ArchitectureInteriorRoomDetails
  | ArchitectureSiteDetails | ArchitectureFacadeDetailDetails
  | PrintingDecorativeDetails | PrintingFunctionalDetails | PrintingReplacementDetails
  | PrintingMiniatureDetails | PrintingEnclosureDetails;

export type IntakeSpec = {
  category: CadCategory;
  subType: IntakeSubType;
  qualityLevel: 'draft' | 'standard' | 'manufacturing' | 'extreme';
  core: IntakeCore;
  drawing: DrawingPreferences;
  details: IntakeDetails;
  /** "Anything else?" — free text, max 1000 chars. */
  freeform: string;
  submittedAt: string;
};

/** What the user is designing. Shapes vocabulary, defaults, validation,
 *  and which parts/materials the system prompts encourage. The runtime
 *  CadCategorySchema lives in shared/spec.ts; we re-export the inferred
 *  type at the top of this file to keep the schema/type pair in lockstep
 *  without an import cycle. */

export type Conversation = Database['public']['Tables']['conversations']['Row'] & {
  /** Pointer into the checkpoint tree for /rewind + branching. */
  currentCheckpointId?: string | null;
  /** Design domain. Defaults to "furniture" on old conversations. */
  category?: CadCategory;
  /** Set once at conversation creation by the intake form. Optional for back-compat. */
  intake?: IntakeSpec;
  /** User-facing language for chat replies, questions, and generated summaries. */
  locale?: AppLocale;

  // ── Mesh mode (Tripo image-to-3D) ──────────────────────────────────────
  /** 'scad' (default, parametric) or 'mesh' (Tripo-generated GLB). */
  mode?: 'scad' | 'mesh';
  /** Tripo task_id while a mesh generation is in flight. */
  pendingMeshId?: string;
  /** Tripo task_id of the active mesh. */
  currentMeshId?: string;
  /** Storage path to the active GLB. */
  currentMeshPath?: string;
  /** Storage path to Tripo's rendered_image preview, optional. */
  currentMeshPreviewPath?: string;
  /** imageId the mesh was generated from. */
  sourceImageId?: string;
  /** e.g. 'tripo:v3.1-20260211' */
  meshModel?: string;
  meshTextureQuality?: 'standard' | 'detailed';
  /** ISO timestamp of the latest successful generation. */
  meshGeneratedAt?: string;
};

/**
 * One issue surfaced by the B6.6 strict-profile validator.
 *
 * Mirrors `services/techdraw/validation_types.py::ValidationIssue`. Field
 * names use camelCase on the wire even though the Python source is
 * snake_case -- the `mark_succeeded` writer and HTTP response transform
 * keys when needed (camelCase keys: `featureId`).
 */
export type DrawingValidationIssue = {
  /** Stable rule id, e.g. 'hole_table_present'. */
  rule: string;
  /** error blocks strict acceptance; warning/info are advisory. */
  severity: 'error' | 'warning' | 'info';
  /** One-line user-facing description. */
  message: string;
  /** If the issue ties to a specific feature, joint, or part. */
  featureId?: string;
  /** If the issue ties to a specific drawing callout (rev 3.2 spec). */
  calloutId?: string;
  /** If the issue points at a part as a whole. */
  partId?: string;
  /** Datum-chain issues that name a missing letter referenced by other FCFs. */
  datumLabel?: string;
  /** Optional fix suggestion. */
  suggestion?: string;
};

/**
 * Drawing-job document, written to:
 *   conversations/{conversationId}/drawingJobs/{jobId}
 *
 * The frontend subscribes to this doc for live status. Cloud Run updates it
 * when the render finishes (or fails). The job is created by the
 * `renderDrawing` Cloud Function before enqueueing the Cloud Tasks task.
 */
export type DrawingJob = {
  /** Job UUID. Same as the doc id. */
  jobId: string;
  /** Conversation under which this job lives. */
  conversationId: string;
  /** Originating message id (the assistant message whose artifact this drawing belongs to). */
  messageId: string;
  /** Optional artifact id from the message's `Content.artifact`. */
  artifactId?: string;
  /** Job status. Cloud Run advances this. */
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  /** Requested output formats. */
  outputs: Array<'svg' | 'pdf' | 'dxf'>;
  /** Fingerprint of spec/parameters/drawing prefs used to create this job. */
  renderFingerprint?: string;
  /** Storage paths of the rendered files, by format. Filled when status = 'succeeded'. */
  files?: Partial<Record<'svg' | 'pdf' | 'dxf', string>>;
  /** Error message if status = 'failed'. */
  errorMessage?: string;
  /** Cloud Run elapsed seconds for the render itself. */
  elapsedSeconds?: number;
  /** ISO timestamps. */
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  /** User who triggered the job (for audit). */
  userId: string;
  /** B6.6 validator output. Present once status === 'succeeded'. */
  violations?: DrawingValidationIssue[];
  /** B6.6: true iff jisStrict mode AND no error-severity issues. */
  strictAccepted?: boolean;
  /** B6.6: validator profile id, e.g. 'jis_oriented_strict_v1'. */
  validationProfile?: string;
};
